// frontend/packages/business-console/src/components/build-interview/BuildingScreen.jsx
//
// Post-confirm "Building your site" screen (Website Builder Redesign, Phase 1).
// Keeps the proven polling from the old BuildProgress (poll the draft every few
// seconds; route to the review page the moment the build leaves the building
// states; show a failure state on failed_generation, and a "we are fixing it"
// state on failed_infra, which only an operator can re-kick) and dresses it as the
// redesign's two-column assembly view: a status column (pill, stage checklist,
// progress) plus the AssemblyPreview.
//
// The stage checklist is a CLIENT-SIDE simulated animation (scoping decision:
// the backend reports start/end, not per-stage events). It advances on a timer
// and holds at the last stage until the real job finishes, then the poll routes
// away. Colors via useTokens; copy is em-dash-free.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Alert, Button } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import GroupsIcon from '@mui/icons-material/Groups';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import useTokens from '../primitives/useTokens';
import { ProgressBar } from './InterviewShell';
import AssemblyPreview from './AssemblyPreview';
import { getBuildDraft, retryBuild } from '../../services/buildFunnelService';

const BUILDING_STATES = new Set(['generating', 'tenant_provisioned']);
// Setup (provisioning) failed. The customer cannot retry it; the operator
// alert Task is raised server-side and an operator re-kick moves the job back
// to tenant_provisioned, so we keep polling (slower) and resume on our own.
const INFRA_FAILED = 'failed_infra';
const INFRA_POLL_MS = 15000;

const STAGES = [
  'Reading your brief',
  'Designing the layout',
  'Writing your homepage copy',
  'Building your service and area pages',
  'Wiring up the contact form and SEO',
];

export default function BuildingScreen({ teamForkIntro }) {
  const t = useTokens();
  const navigate = useNavigate();
  const location = useLocation();
  const [draft, setDraft] = useState(null);
  const [slow, setSlow] = useState(false);
  const [sim, setSim] = useState(0); // 0..1 simulated progress (caps short of 1)
  const [retrying, setRetrying] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0); // bump = restart the poll
  const failed = draft && draft.state === 'failed_generation';
  const infraFailed = draft && draft.state === INFRA_FAILED;
  const failedRef = useRef(false);
  failedRef.current = Boolean(failed || infraFailed);

  // Poll the real job (ported from the prior BuildProgress).
  useEffect(() => {
    let active = true;
    let timer;
    const startedAt = Date.now();
    const reviewPath = `${location.pathname.replace(/\/+$/, '')}/review`;
    const tick = async () => {
      let next;
      try {
        next = await getBuildDraft();
      } catch {
        // transient: keep polling
      }
      if (!active) return;
      if (next) setDraft(next);
      if (
        next &&
        next.exists &&
        next.state &&
        !BUILDING_STATES.has(next.state) &&
        next.state !== 'failed_generation' &&
        next.state !== INFRA_FAILED
      ) {
        navigate(reviewPath);
        return;
      }
      if (next && next.state === 'failed_generation') return;
      if (next && next.state === INFRA_FAILED) {
        timer = setTimeout(tick, INFRA_POLL_MS);
        return;
      }
      if (Date.now() - startedAt > 8 * 60 * 1000) setSlow(true);
      timer = setTimeout(tick, 4000);
    };
    tick();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [navigate, location.pathname, retryNonce]);

  // Simulated stage progress: ease toward ~0.92 and hold until the real job
  // finishes (the poll routes away) or fails (freeze).
  useEffect(() => {
    const id = setInterval(() => {
      setSim((s) => {
        if (failedRef.current) return s;
        return s >= 0.92 ? s : s + 0.02;
      });
    }, 650);
    return () => clearInterval(id);
  }, []);

  const onRetry = async () => {
    setRetrying(true);
    try {
      await retryBuild();
      // Leave the failure screen and resume the building poll. The worker
      // re-enters generating; bumping the nonce restarts the poll loop, which
      // routes to review the moment the draft is ready.
      setSim(0);
      setDraft(null);
      setRetryNonce((n) => n + 1);
    } catch {
      // Stay on the failure screen; the button re-enables so they can retry.
    } finally {
      setRetrying(false);
    }
  };

  if (failed) {
    return (
      <Box>
        <Typography
          sx={{ fontSize: 18, fontWeight: 900, color: t.text, mb: 1 }}
        >
          We hit a snag building your site
        </Typography>
        <Alert severity="warning">
          Something went wrong while building your site. Your answers are saved
          and our team has been alerted. Most snags clear on a retry, so you can
          try again now, or leave it with us and we will reach out shortly.
        </Alert>
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="contained"
            onClick={onRetry}
            disabled={retrying}
            sx={{ fontWeight: 800, backgroundImage: t.gradient }}
          >
            {retrying ? 'Retrying...' : 'Try again'}
          </Button>
          <Typography sx={{ fontSize: 12.5, color: t.textSecondary }}>
            This usually fixes a temporary hiccup. Your answers are saved.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (infraFailed) {
    return (
      <Box>
        <Typography
          sx={{ fontSize: 18, fontWeight: 900, color: t.text, mb: 1 }}
        >
          We hit a snag setting up your site
        </Typography>
        <Alert severity="warning">
          Something went wrong while we were setting up your site. Your answers
          are saved, our team has been alerted, and we are on it. You do not
          need to do anything. This page picks up again on its own once it is
          fixed, and we will reach out if we need anything from you.
        </Alert>
      </Box>
    );
  }

  const reached = Math.min(STAGES.length - 1, Math.floor(sim * STAGES.length));
  const percent = Math.min(92, Math.round(sim * 100));

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' },
        gap: 2,
        alignItems: 'start',
      }}
    >
      <Box>
        <Box
          sx={{
            display: 'inline-block',
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: t.purple,
            background: t.purpleBg,
            border: `1px solid ${t.purpleBorder}`,
            borderRadius: '999px',
            px: 1.25,
            py: 0.5,
            mb: 1.5,
          }}
        >
          Building now
        </Box>
        <Typography
          sx={{ fontSize: 21, fontWeight: 900, color: t.text, mb: 0.75 }}
        >
          We are building your site
        </Typography>
        <Typography sx={{ fontSize: 13, color: t.textSecondary, mb: 2 }}>
          This usually takes a few minutes. Stay on this page and we will take
          you straight to it the moment it is ready.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.25,
            p: 1.5,
            mb: 2,
            borderRadius: '12px',
            background: t.card,
            border: `1px solid ${t.cardBorder}`,
          }}
        >
          <EditOutlinedIcon sx={{ fontSize: 18, color: t.muted, mt: 0.25 }} />
          <Typography
            sx={{ fontSize: 12.5, color: t.textSecondary, lineHeight: 1.5 }}
          >
            Changed your mind? You are not locked in. Once this draft is ready
            you can edit any answer and rebuild, or tweak the finished site
            directly.
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <ProgressBar percent={percent} />
          <Typography
            sx={{ fontSize: 12, fontWeight: 700, color: t.muted, mt: 0.5 }}
          >
            {percent}%
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {STAGES.map((label, i) => {
            const state =
              i < reached ? 'done' : i === reached ? 'current' : 'upcoming';
            return (
              <Box
                key={label}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: state === 'done' ? t.gradient : t.card,
                    border:
                      state === 'current'
                        ? `2px solid ${t.blue}`
                        : state === 'upcoming'
                          ? `1px solid ${t.grayBorder}`
                          : 'none',
                  }}
                >
                  {state === 'done' ? (
                    <CheckIcon sx={{ fontSize: 13, color: '#fff' }} />
                  ) : state === 'current' ? (
                    <Box
                      data-bi-anim
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: t.blue,
                        animation: 'bi-softpulse 1.4s ease-in-out infinite',
                      }}
                    />
                  ) : null}
                </Box>
                <Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: state === 'current' ? 800 : 600,
                    color:
                      state === 'upcoming'
                        ? t.faint
                        : state === 'current'
                          ? t.blue
                          : t.textSecondary,
                  }}
                >
                  {label}
                </Typography>
                {state === 'current' ? (
                  <Typography
                    sx={{ fontSize: 11, fontWeight: 700, color: t.blue }}
                  >
                    Now
                  </Typography>
                ) : null}
              </Box>
            );
          })}
        </Box>

        {teamForkIntro ? (
          <Alert severity="info" icon={<GroupsIcon />} sx={{ mt: 2 }}>
            {teamForkIntro}
          </Alert>
        ) : null}
        {slow ? (
          <Typography sx={{ fontSize: 11.5, color: t.faint, mt: 1.5 }}>
            This is taking longer than usual. It is still working. You can leave
            and come back, or refresh in a minute.
          </Typography>
        ) : null}
      </Box>

      <AssemblyPreview />
    </Box>
  );
}
